import React from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Button,
  Card,
  CardContent,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ElectricalServicesIcon from '@mui/icons-material/ElectricalServices';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import EngineeringIcon from '@mui/icons-material/Engineering';
import BuildIcon from '@mui/icons-material/Build';
import SpeedIcon from '@mui/icons-material/Speed';
import { colors } from '../theme';

const Services: React.FC = () => {
  const services = [
    {
      icon: <PrecisionManufacturingIcon sx={{ fontSize: 48 }} />,
      title: 'New Product Selection',
      description: "We're here to help you select the right electrical components for your projects. We're partners with many industry experts who can offer you deeper product knowledge.",
    },
    {
      icon: <EngineeringIcon sx={{ fontSize: 48 }} />,
      title: 'Application Assistance',
      description: 'With over 50 years of experience, our expertise and diverse team of professionals are the best resource for helping you determine which Variable Frequency Drive (VFD) is the right choice.',
    },
    {
      icon: <SettingsSuggestIcon sx={{ fontSize: 48 }} />,
      title: 'Product Technology Migration',
      description: 'As the years go by technology continues to evolve and improve. We can help you replace those "legacy" components with the latest technology.',
    },
    {
      icon: <SpeedIcon sx={{ fontSize: 48 }} />,
      title: 'VFD Commissioning Support',
      description: "We're real people who can come to your site and help install or program that new VFD or soft start. We can also help you upgrade your next control panel.",
    },
    {
      icon: <ElectricalServicesIcon sx={{ fontSize: 48 }} />,
      title: 'VFD Retrofit Consulting',
      description: "Whether it's an MCC bucket, a machine from overseas, or just a control cabinet from the 80's, we can help you quickly replace that legacy or damaged component.",
    },
    {
      icon: <BuildIcon sx={{ fontSize: 48 }} />,
      title: 'Control Panel Fabrication',
      description: 'Let us help you design your next control panel. We specialize in VFD repackaging, PLC panels, and pump control panels of all sizes.',
    },
    {
      icon: <PrecisionManufacturingIcon sx={{ fontSize: 48 }} />,
      title: 'Tank Sizing & Engineering',
      description: 'Get expert help to size or engineer your next tank. We specialize in single and double-wall containment units, horizontal tanks, and chemical feed stations.',
    },
    {
      icon: <EngineeringIcon sx={{ fontSize: 48 }} />,
      title: 'Onsite Services',
      description: 'We offer VFD commissioning, VFD retrofits, and harmonic reporting as well as control panel diagnosis and repair services.',
    },
  ];

  const manufacturers = [
    'Danfoss',
    'TCI (Transcoil)',
    'Pfannenberg',
    'Bonitron',
    'PULS',
    'Lovato',
    'Macromatic',
    'Socomec',
    'Citel',
    'AttaBox'
  ];

  return (
    <Box sx={{ overflow: 'hidden' }}>
      {/* Page Header */}
      <Box
        sx={{
          py: { xs: 8, md: 10 },
          background: `linear-gradient(135deg, ${colors.secondary} 0%, ${colors.secondaryDark} 100%)`,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            right: 0,
            width: '40%',
            height: '100%',
            background: `linear-gradient(135deg, transparent 0%, rgba(245, 166, 35, 0.1) 100%)`,
          },
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Typography
              variant="overline"
              sx={{ color: colors.primary, fontWeight: 600, letterSpacing: 2, mb: 1, display: 'block' }}
            >
              What We Do
            </Typography>
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                color: '#FFFFFF',
                fontWeight: 700,
                mb: 2,
              }}
            >
              Services
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: 'rgba(255, 255, 255, 0.8)',
                fontSize: '1.1rem',
                maxWidth: 500,
              }}
            >
              Complete electrical engineering solutions for your business
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Accent Line */}
      <Box sx={{ height: 4, background: `linear-gradient(90deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)` }} />

      {/* Intro Section */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          background: '#FFFFFF',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={8} alignItems="center">
            <Grid item xs={12} md={7}>
              <Typography
                variant="overline"
                sx={{ color: colors.primary, fontWeight: 600, letterSpacing: 2 }}
              >
                Our Approach
              </Typography>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: '2rem', md: '2.5rem' },
                  color: colors.secondary,
                  mb: 4,
                  mt: 1,
                }}
              >
                Complete Electrical Engineering Solutions
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: colors.textSecondary,
                  fontSize: '1.1rem',
                  lineHeight: 1.9,
                  mb: 3,
                }}
              >
                Process Control takes the work out of the process for you. We have deep product
                knowledge and strong relationships with all the manufacturers we represent and
                can save you valuable resources spent on searching the web and hoping you stumble
                across the right solution.
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: colors.textSecondary,
                  fontSize: '1.1rem',
                  lineHeight: 1.9,
                  mb: 4,
                }}
              >
                When you work with us, we put you first. We take the part information you come
                to us with and search through the vast inventory of all the manufacturers we
                represent until we find exactly what you need.
              </Typography>
              <Button
                component={Link}
                to="/contact"
                variant="contained"
                endIcon={<ArrowForwardIcon />}
              >
                Get Started
              </Button>
            </Grid>
            <Grid item xs={12} md={5}>
              <Box
                sx={{
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: 24,
                    left: 24,
                    right: -24,
                    bottom: -24,
                    border: `3px solid ${colors.primary}`,
                    borderRadius: 4,
                    zIndex: -1,
                  },
                }}
              >
                <Box
                  component="img"
                  src="/vfd-panel-1.jpg"
                  alt="Control Panel"
                  sx={{
                    width: '100%',
                    borderRadius: 4,
                    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Services Grid */}
      <Box
        sx={{
          py: { xs: 8, md: 10 },
          background: colors.background,
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="overline"
              sx={{ color: colors.primary, fontWeight: 600, letterSpacing: 2 }}
            >
              Our Services
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '2rem', md: '2.5rem' },
                color: colors.secondary,
                mt: 1,
              }}
            >
              How We Can Help You
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {services.map((service, index) => (
              <Grid item xs={12} sm={6} lg={3} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    transition: 'all 0.3s ease',
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: colors.border,
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: `0 20px 50px rgba(245, 166, 35, 0.15)`,
                      borderColor: colors.primary,
                      '& .service-icon': {
                        color: colors.primary,
                        transform: 'scale(1.1)',
                      },
                    },
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box
                      className="service-icon"
                      sx={{
                        color: colors.secondary,
                        mb: 2,
                        transition: 'all 0.3s ease',
                      }}
                    >
                      {service.icon}
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{
                        color: colors.secondary,
                        fontWeight: 700,
                        mb: 1.5,
                        fontSize: '1.1rem',
                      }}
                    >
                      {service.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: colors.textSecondary,
                        lineHeight: 1.7,
                      }}
                    >
                      {service.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Manufacturers Section */}
      <Box
        sx={{
          py: { xs: 8, md: 10 },
          background: '#FFFFFF',
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="overline"
              sx={{ color: colors.primary, fontWeight: 600, letterSpacing: 2 }}
            >
              Our Partners
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '2rem', md: '2.5rem' },
                color: colors.secondary,
                mt: 1,
                mb: 2,
              }}
            >
              Manufacturers We Represent
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: colors.textSecondary,
                maxWidth: 700,
                mx: 'auto',
                fontSize: '1.1rem',
              }}
            >
              We represent 13 different manufacturers that offer a range of products to serve
              a variety of different industries.
            </Typography>
          </Box>

          <Grid container spacing={2} justifyContent="center">
            {manufacturers.map((manufacturer, index) => (
              <Grid item xs={6} sm={4} md={3} key={index}>
                <Box
                  component={Link}
                  to="/suppliers"
                  sx={{
                    display: 'block',
                    p: 3,
                    textAlign: 'center',
                    textDecoration: 'none',
                    background: colors.background,
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: colors.border,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      background: colors.primary,
                      borderColor: colors.primary,
                      transform: 'translateY(-4px)',
                      boxShadow: `0 12px 30px rgba(245, 166, 35, 0.3)`,
                      '& .manufacturer-name': {
                        color: '#FFFFFF',
                      },
                    },
                  }}
                >
                  <Typography
                    className="manufacturer-name"
                    sx={{
                      fontWeight: 600,
                      color: colors.secondary,
                      transition: 'color 0.3s ease',
                    }}
                  >
                    {manufacturer}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          py: { xs: 10, md: 12 },
          background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)`,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '2rem', md: '2.75rem' },
                color: '#FFFFFF',
                mb: 3,
              }}
            >
              Ready to Get Started?
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: 'rgba(255, 255, 255, 0.9)',
                fontSize: '1.15rem',
                mb: 4,
                maxWidth: 600,
                mx: 'auto',
              }}
            >
              Contact us today for a free consultation and quote.
              We respond to most inquiries within 24 hours.
            </Typography>
            <Button
              component={Link}
              to="/contact"
              variant="contained"
              size="large"
              endIcon={<ArrowForwardIcon />}
              sx={{
                backgroundColor: '#FFFFFF',
                color: colors.primary,
                px: 5,
                py: 1.5,
                fontSize: '1rem',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  boxShadow: '0 8px 30px rgba(255, 255, 255, 0.3)',
                },
              }}
            >
              Request a Quote
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Services;
